import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// ⚠️ MIRROR / DRIFT-GUARDED. This renderer must stay visually IDENTICAL to the
// online production player (backend app/(player)/play/[sessionId]/page.tsx) and
// the backend admin release-preview. Any visual geometry / stage-model change
// here MUST be (1) reflected in ./stage-contract.json, (2) ported to the backend
// renderer, (3) the contract copied byte-for-byte to the backend repo.
// stage-contract.test.ts fails the build if this file stops matching the contract.
/**
 * PlayerStage — the production VN render layer, lifted from the online
 * single-visible player so the in-IDE / web previews are visually IDENTICAL to
 * the shipped player.
 *
 * Single-visible: the stage carries at most ONE character portrait
 * (`stage.visible`), rendered via ONE `CharacterSlot`. There is NO left/right
 * companion derivation and NO `forceAllVisible` — the visible char is placed on
 * the LEFT when it is the protagonist, else on the RIGHT (the 2-value bubble-
 * side law), and shows whenever present and not scene-revealing.
 *
 * What's ported VERBATIM (geometry, class strings, z-orders, animations):
 *   - BgLayer            (preload via new Image, 2-stage sceneZoom pan, kenBurns,
 *                         crossfade, gradient fallback, + IDE bg-missing card)
 *   - CharacterSlot      (figma 705×1253 aspect box = 179.39%W, aspectRatio
 *                         705/1253, side bleed left:-58.02% / -16.28%,
 *                         single-visible opacity fade, 3-layer transform stack,
 *                         motion-breathe, speaker-pop)
 *   - CharPortrait       (decode-gate fade-in, object-cover, bottom-center
 *                         anchor, initial-badge load fallback, + IDE missing/look
 *                         placeholder)
 *   - DialogueLayer      (BUBBLE_TOP_PCT = 62, per-kind reserve, pin-to-speaker-
 *                         wall vs center, typewriter slice via revealCount)
 *   - the choice panel   ("做个选择" eyebrow, paper-stamp buttons, D20 Chip)
 *   - the cg overlay     (fullscreen CgMedia + 继续, video-end gate)
 *   - the phone modal    (PhoneCard inside Modal)
 *   - TapLayer           (full-screen tap-to-advance + ripple)
 *   - the "Tap to continue" hint
 *   - play-scrim-bottom + play-vignette
 *
 * Out of scope (intentionally dropped): AudioManager, remix long-press /
 * RemixProvider, dream markers, dice modal, HUD (affection / SAN / XP),
 * achievements, checkpoint. The whole stage is wrapped in `.ls-player-root` so
 * the scoped CSS (player.generated.css) applies.
 *
 * This component is PURE render + tap intent. It does NOT own the StepPlayer,
 * cursor, or host messaging — the integrator's store drives `reduce.ts` and
 * feeds the derived props below.
 */
import { useEffect, useRef, useState, } from "react";
import { Chip, DialogueBubble, Modal, NameTag, PaperButton, PhoneCard, pastelFor, } from "./primitives.js";
import { CgMedia } from "./cg-media.js";
import { detectMediaKind } from "./media-kind.js";
import { t } from "./locale.js";
// ─────────────────────────────────────────────────────────────
// Constants — VERBATIM from the online player
// ─────────────────────────────────────────────────────────────
const BUBBLE_SIDE_GUTTER_PX = 28;
const NAMETAG_RESERVE_PX = 26;
const THINKING_PILL_RESERVE_PX = 20;
const BUBBLE_TOP_PCT = 62;
const SPRITE_W = "179.39%"; // 705/393
const SPRITE_ASPECT = "705 / 1253"; // matches 立绘 source aspect, no object-cover crop drift
const SPRITE_TOP = "15.26%"; // 130/852
const EMOTE_GLYPH = {
    anger: "💢",
    sweat: "💦",
    heart: "❤️",
    question: "?",
    exclaim: "!",
    idea: "💡",
    music: "♪",
    doom: "☠",
    ellipsis: "...",
};
// ─────────────────────────────────────────────────────────────
// RemixTag — "✦REMIX" badge for player-authored beats. The narrator remix
// marker in DialogueLayer needs the standalone version, so it's inlined here.
// ─────────────────────────────────────────────────────────────
function RemixTag({ className }) {
    return (_jsx("span", { className: "select-none inline-flex items-center" + (className ? " " + className : ""), style: {
            background: "var(--paper)",
            border: "1px solid var(--ink)",
            borderRadius: "999px",
            padding: "1px 9px 2px",
            height: "20px",
            fontFamily: '"Playfair Display", "Bodoni 72", "Georgia", serif',
            fontSize: "12px",
            fontStyle: "italic",
            fontWeight: 900,
            letterSpacing: "0.14em",
            color: "var(--ink)",
            boxShadow: "0 1.5px 0 var(--ink)",
            whiteSpace: "nowrap",
            lineHeight: 1,
        }, children: "\u2726REMIX" }));
}
// ─────────────────────────────────────────────────────────────
// BgLayer — VERBATIM (+ IDE bg-missing placeholder + language)
// ─────────────────────────────────────────────────────────────
function BgLayer({ url, epoch, missing, name, language }) {
    const [currentUrl, setCurrentUrl] = useState(null);
    const [prevUrl, setPrevUrl] = useState(null);
    const [startEdge, setStartEdge] = useState("left");
    const timerRef = useRef(null);
    useEffect(() => {
        if (!url || missing) {
            setCurrentUrl(null);
            return;
        }
        if (url === currentUrl)
            return;
        // preload before swap: only commit url on successful load — broken/404
        // images keep the existing bg (or gradient fallback).
        const img = new Image();
        img.onload = () => {
            setStartEdge(Math.random() < 0.5 ? "left" : "right");
            setPrevUrl(currentUrl);
            setCurrentUrl(url);
            if (timerRef.current)
                clearTimeout(timerRef.current);
            timerRef.current = setTimeout(() => setPrevUrl(null), 2000);
        };
        img.onerror = () => {
            if (typeof console !== "undefined")
                console.warn("[BgLayer] bg load failed:", url);
        };
        img.src = url;
        return () => {
            if (timerRef.current)
                clearTimeout(timerRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [url, epoch]);
    if (missing) {
        return (_jsx("div", { style: { position: "absolute", inset: 0, zIndex: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "#12161f" }, children: _jsxs("div", { style: { display: "flex", flexDirection: "column", alignItems: "center", gap: 8, padding: "16px 18px", borderRadius: 12, background: "rgba(250,238,218,0.1)", border: "1px solid rgba(239,159,39,0.55)", color: "#f0c98a", maxWidth: "78%", textAlign: "center" }, children: [_jsx("span", { style: { fontSize: 13, fontWeight: 500 }, children: t(language, "player.bgMissing") }), name ? _jsx("span", { style: { fontFamily: "ui-monospace, SFMono-Regular, monospace", fontSize: 12, wordBreak: "break-all" }, children: name }) : null] }) }));
    }
    if (!currentUrl && !prevUrl) {
        return (_jsx("div", { className: "absolute inset-0 z-0 bg-gradient-to-br from-[#1e2a3a] via-[#12161f] to-[#0a0c12]" }));
    }
    const sceneZoomKeyframe = startEdge === "left" ? "sceneZoomLeft" : "sceneZoomRight";
    return (_jsxs("div", { className: "absolute inset-0 z-0 overflow-hidden", children: [prevUrl && (_jsx("div", { className: "absolute inset-0", style: {
                    backgroundImage: `url('${prevUrl}')`,
                    backgroundSize: "cover",
                    backgroundPosition: "center center",
                    opacity: 0,
                    transition: "opacity 800ms ease",
                } }, `bg-prev-${prevUrl}-${epoch}`)), currentUrl && (_jsx("div", { className: "absolute inset-0", style: {
                    backgroundImage: `url('${currentUrl}')`,
                    backgroundSize: "cover",
                    backgroundPositionY: "center",
                    animation: `${sceneZoomKeyframe} 2000ms ease-out both, kenBurns 22s ease-in-out 2000ms infinite alternate`,
                    willChange: "transform, opacity, background-position",
                } }, `bg-cur-${currentUrl}-${epoch}`))] }));
}
// ─────────────────────────────────────────────────────────────
// CharPortrait — decode-gate fade-in + object-cover (online "latest"), plus the
// IDE missing/look placeholder. Single-visible: no `side` prop (anchor is
// always bottom-center; the side bleed lives on the wrapper).
// ─────────────────────────────────────────────────────────────
function getInitial(name) {
    const trimmed = name.trim();
    return trimmed ? trimmed[0].toUpperCase() : "?";
}
function CharPortrait({ slot, language }) {
    const [failedUrl, setFailedUrl] = useState(null);
    const failed = failedUrl === slot.url;
    // decode-gate state — fade the portrait in only once the bytes are decoded,
    // so a freshly-shown sprite never flashes half-loaded.
    const [decoded, setDecoded] = useState(false);
    const [decodedUrl, setDecodedUrl] = useState(slot.url);
    if (slot.url !== decodedUrl) {
        setDecodedUrl(slot.url);
        setDecoded(false);
    }
    const objectPosition = "bottom center";
    // IDE authoring nuance — keep (uses optional StageVisibleChar.missing/look).
    if (slot.missing) {
        return (_jsx("div", { style: { pointerEvents: "none", display: "flex", height: "100%", width: "100%", flexDirection: "column", alignItems: "center", justifyContent: "flex-end", paddingBottom: "18%" }, children: _jsxs("div", { style: { display: "flex", flexDirection: "column", alignItems: "center", gap: 6, padding: "12px 14px", borderRadius: 12, background: "rgba(250,238,218,0.1)", border: "1px dashed rgba(239,159,39,0.55)", color: "#f0c98a", maxWidth: "92%", textAlign: "center" }, children: [_jsx("span", { style: { fontSize: 12, fontWeight: 500 }, children: t(language, "player.spriteMissing") }), _jsx("span", { style: { fontFamily: "ui-monospace, SFMono-Regular, monospace", fontSize: 11, wordBreak: "break-all" }, children: slot.look ? `${slot.character} / ${slot.look}` : slot.character })] }) }));
    }
    if (failed) {
        const initial = getInitial(slot.character);
        return (_jsxs("div", { className: "pointer-events-none flex h-full w-full flex-col items-center justify-end pb-[18%]", children: [_jsx("div", { className: "flex h-28 w-28 items-center justify-center rounded-full", style: { background: pastelFor(slot.character) }, children: _jsx("span", { className: "text-5xl font-black text-[var(--ink)]", children: initial }) }), _jsx("div", { className: "mt-2 rounded-full bg-white/85 px-3 py-1 text-[10px] font-black uppercase tracking-[0.14em] text-[var(--ink-muted)]", children: slot.character })] }));
    }
    return (_jsx("div", { className: "pointer-events-none relative h-full w-full", children: _jsx("img", { src: slot.url, alt: slot.character, decoding: "async", ref: (node) => {
                // If the image was cached + already complete before onLoad fires,
                // reveal it immediately.
                if (node && node.complete && node.naturalWidth > 0 && !decoded) {
                    setDecoded(true);
                }
            }, onLoad: (e) => {
                const img = e.currentTarget;
                if (typeof img.decode === "function") {
                    img.decode().then(() => setDecoded(true)).catch(() => setDecoded(true));
                }
                else {
                    setDecoded(true);
                }
            }, onError: () => setFailedUrl(slot.url), className: "h-full w-full object-cover", style: { objectPosition, opacity: decoded ? 1 : 0, transition: "opacity 240ms ease-out" } }, slot.url) }));
}
// ─────────────────────────────────────────────────────────────
// CharacterSlot — single-visible. side is "left" | "right" only (no center).
// Aspect-locked box (no fixed height) + object-cover normalizes mixed source
// dims so all sprites land the same on-screen size.
// ─────────────────────────────────────────────────────────────
function CharacterSlot({ slot, side, isSpeaker, isActive, language, }) {
    const visible = !!slot && isActive;
    const offTransform = "translate(0, 0)";
    const onTransform = "translate(0, 0)";
    const transition = visible ? "opacity 600ms ease-out" : "opacity 400ms ease-in";
    const popRef = useRef(null);
    const prevIsSpeakerRef = useRef(false);
    useEffect(() => {
        if (!popRef.current)
            return;
        if (isSpeaker && !prevIsSpeakerRef.current && visible) {
            popRef.current.animate([
                { transform: "translateY(0) scale(1)" },
                { transform: "translateY(-3px) scale(1.012)", offset: 0.35 },
                { transform: "translateY(1px) scale(0.998)", offset: 0.7 },
                { transform: "translateY(0) scale(1)" },
            ], { duration: 280, easing: "cubic-bezier(.34, 1.56, .64, 1)" });
        }
        prevIsSpeakerRef.current = isSpeaker;
    }, [isSpeaker, visible]);
    const anchorStyle = {
        left: side === "left" ? "-58.02%" : "-16.28%", // -228/393 / -64/393
        top: SPRITE_TOP,
        width: SPRITE_W,
        aspectRatio: SPRITE_ASPECT,
        transform: visible ? onTransform : offTransform,
        transformOrigin: `bottom ${side}`,
    };
    return (_jsx("div", { className: "pointer-events-none absolute z-[5]", "data-character-name": slot?.character ?? "", "data-character-side": side, "data-active": visible ? "true" : "false", style: {
            ...anchorStyle,
            opacity: visible ? 1 : 0,
            transition,
        }, children: slot && (_jsx("div", { ref: popRef, className: "h-full w-full", style: { transformOrigin: "bottom center" }, children: _jsx("div", { className: "motion-breathe h-full w-full", children: _jsx(CharPortrait, { slot: slot, language: language }, `ch-${slot.epoch}`) }) })) }));
}
// ─────────────────────────────────────────────────────────────
// DialogueLayer — VERBATIM
// ─────────────────────────────────────────────────────────────
function DialogueLayer({ line, revealCount, isTyping, protagonistName, hasActivePortrait, }) {
    if (line.kind === "phone")
        return null;
    const shown = (full) => revealCount >= full.length ? full : full.slice(0, Math.max(0, revealCount));
    const anchorStyle = line.kind === "dialogue" && hasActivePortrait
        ? line.side === "left"
            ? { left: BUBBLE_SIDE_GUTTER_PX, right: "auto" }
            : { right: BUBBLE_SIDE_GUTTER_PX, left: "auto" }
        : { left: "50%", transform: "translateX(-50%)" };
    return (_jsxs("div", { className: "pointer-events-none absolute z-20", style: {
            top: (() => {
                const reserve = line.kind === "dialogue"
                    ? NAMETAG_RESERVE_PX
                    : line.kind === "you"
                        ? THINKING_PILL_RESERVE_PX
                        : 0; // narrator
                const shiftPx = NAMETAG_RESERVE_PX - reserve;
                return `calc(${BUBBLE_TOP_PCT}% + ${shiftPx}px)`;
            })(),
            width: "317px",
            maxWidth: `calc(100% - ${BUBBLE_SIDE_GUTTER_PX * 2}px)`,
            ...anchorStyle,
        }, children: [line.fromRemix && line.kind === "narrator" && (_jsx("div", { className: "absolute -top-5 left-1/2 z-[1] -translate-x-1/2", children: _jsx(RemixTag, {}) })), line.kind === "narrator" && (_jsx(DialogueBubble, { kind: "narrator", text: shown(line.text), isTyping: isTyping })), line.kind === "you" && (_jsx(DialogueBubble, { kind: "thinking", speaker: protagonistName ?? undefined, text: shown(line.text), isTyping: isTyping, fromRemix: line.fromRemix })), line.kind === "dialogue" && (_jsx(DialogueBubble, { kind: line.side === "left" ? "dialogue-left" : "dialogue-right", speaker: line.speaker, speakerColor: line.speakerColor, text: shown(line.text), isTyping: isTyping, fromRemix: line.fromRemix }))] }));
}
// ─────────────────────────────────────────────────────────────
// EmoteBubble — VERBATIM (the emotePop keyframe is inlined as a <style>,
// scoped to this element via a data attribute).
// ─────────────────────────────────────────────────────────────
function EmoteBubble({ kind, screenSide }) {
    const anchor = screenSide === "left" ? { left: "20%", right: "auto" } : { right: "20%", left: "auto" };
    return (_jsxs("div", { "data-emote-bubble": true, className: "pointer-events-none absolute z-[8] flex h-12 w-12 items-center justify-center rounded-full paper-lift", style: {
            background: "var(--paper)",
            top: "30%",
            ...anchor,
            animation: "emotePop 300ms ease-out both",
        }, children: [_jsx("span", { className: "text-xl font-black", children: EMOTE_GLYPH[kind] }), _jsx("style", { children: `
        @keyframes emotePop {
          0%   { transform: scale(0.4) translateY(6px); opacity: 0; }
          60%  { transform: scale(1.15) translateY(0); opacity: 1; }
          100% { transform: scale(1) translateY(0); opacity: 1; }
        }
      ` })] }));
}
// ─────────────────────────────────────────────────────────────
// TapLayer — full-screen tap-to-advance + ripple. Remix long-press dropped
// (out of scope) so this is the short-tap path only.
// ─────────────────────────────────────────────────────────────
function TapLayer({ blocked, onTap, language }) {
    // Tap ripple — pure DOM append, no state churn (verbatim spawnRipple).
    const spawnRipple = (e) => {
        const host = e.currentTarget;
        const rect = host.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        const ripple = document.createElement("span");
        ripple.className = "tap-ripple";
        ripple.style.left = `${x}px`;
        ripple.style.top = `${y}px`;
        host.appendChild(ripple);
        window.setTimeout(() => ripple.remove(), 560);
    };
    return (_jsx("button", { onClick: (e) => {
            if (blocked)
                return;
            spawnRipple(e);
            onTap();
        }, disabled: blocked, className: "absolute inset-0 z-10 overflow-hidden" + (blocked ? " pointer-events-none" : ""), "aria-label": t(language, "player.continue") }));
}
// ─────────────────────────────────────────────────────────────
// PlayerStage — the full stage. Render + tap intent only.
// ─────────────────────────────────────────────────────────────
export function PlayerStage({ stage, protagonistName, language, revealCount, isTyping, decision, cgVideoEnded, blocked, onTap, onSelectChoice, onCgContinue, onCgVideoEnded, }) {
    // Scene-reveal gate — VERBATIM behavior: when bgEpoch changes (after the
    // first observed epoch), blank the portrait + dialogue for 2s while the bg's
    // 2-stage zoom plays.
    const [sceneRevealing, setSceneRevealing] = useState(false);
    const lastBgEpochRef = useRef(0);
    useEffect(() => {
        if (lastBgEpochRef.current === 0) {
            lastBgEpochRef.current = stage.bgEpoch;
            return;
        }
        if (stage.bgEpoch === lastBgEpochRef.current)
            return;
        lastBgEpochRef.current = stage.bgEpoch;
        if (!stage.bgUrl)
            return;
        setSceneRevealing(true);
        const t = setTimeout(() => setSceneRevealing(false), 2000);
        return () => clearTimeout(t);
    }, [stage.bgEpoch, stage.bgUrl]);
    // ── single-visible derivation ──
    // The one on-screen char stands on the LEFT when it is the protagonist, else
    // on the RIGHT (the 2-value bubble-side law).
    const visible = stage.visible;
    const visibleSide = visible && protagonistName && visible.character !== protagonistName ? "right" : "left";
    // Active speaker (kind "you" → protagonist). The portrait pops as the speaker
    // only when the visible char IS the active speaker.
    const activeSpeaker = stage.line?.kind === "dialogue"
        ? stage.line.speaker
        : stage.line?.kind === "you"
            ? protagonistName
            : null;
    const hasActivePortrait = !!visible && activeSpeaker === visible.character;
    // Interaction lock — the store's `blocked` OR an open choice/cg decision.
    const isBlocked = !!blocked || decision?.kind === "choice" || decision?.kind === "cg_show";
    return (_jsx("div", { className: "ls-player-root", children: _jsxs("div", { className: "relative h-[100svh] w-full overflow-hidden bg-black", children: [_jsx(BgLayer, { url: stage.bgUrl, epoch: stage.bgEpoch, missing: stage.bgMissing, name: stage.bgName, language: language }), _jsx(CharacterSlot, { slot: visible, side: visibleSide, isSpeaker: !!visible && activeSpeaker === visible.character, isActive: !sceneRevealing && !!visible, language: language }), _jsx("div", { className: "play-scrim-bottom" }), _jsx("div", { className: "play-vignette" }), stage.emote &&
                    (() => {
                        const charSide = stage.emote.side;
                        const screenSide = charSide === "right" ? "right" : "left";
                        return _jsx(EmoteBubble, { kind: stage.emote.kind, screenSide: screenSide });
                    })(), _jsx(TapLayer, { blocked: isBlocked, onTap: onTap, language: language }), stage.line && !decision && !sceneRevealing && (_jsx(DialogueLayer, { line: stage.line, revealCount: revealCount, isTyping: isTyping, protagonistName: protagonistName, hasActivePortrait: hasActivePortrait })), stage.line &&
                    stage.line.kind !== "phone" &&
                    !decision &&
                    !sceneRevealing &&
                    !isTyping && (_jsx("div", { className: "paper-pulse absolute inset-x-0 z-20 text-center text-[13px] font-bold tracking-wide text-white/65", style: {
                        bottom: "max(14px, calc(env(safe-area-inset-bottom, 0px) + 14px))",
                        textShadow: "0 0 8px rgba(255,200,190,0.35), 0 1px 2px rgba(0,0,0,0.4)",
                    }, children: t(language, "player.tapToContinue") })), stage.line?.kind === "phone" && !decision && (_jsx(Modal, { onDismiss: onTap, veil: "default", align: "center", children: _jsx(PhoneCard, { contact: stage.line.contact, messages: stage.line.messages, language: language }) })), decision?.kind === "choice" && (_jsxs("div", { className: "absolute z-30", style: {
                        top: "58%",
                        left: "50%",
                        transform: "translateX(-50%)",
                        width: "317px",
                        maxWidth: "calc(100% - 32px)",
                    }, children: [_jsx("div", { className: "motion-bubble-in mb-3 text-center text-[11px] font-black uppercase tracking-[0.22em] text-white/80", style: { textShadow: "0 1px 3px rgba(0,0,0,0.55), 0 0 12px rgba(255,200,190,0.25)" }, children: t(language, "player.choicePrompt") }), _jsx("div", { className: "flex flex-col gap-3", children: decision.options.map((opt) => (_jsx("div", { className: "motion-choice-in", children: _jsxs("button", { onClick: () => {
                                        if (isBlocked)
                                            return;
                                        onSelectChoice(opt);
                                    }, disabled: !!blocked, "aria-label": opt.text, title: opt.text, className: "paper-stamp paper-stamp-interactive flex h-14 w-full items-center justify-between gap-2 rounded-[22px] px-6 text-[17px] font-black tracking-tight text-[var(--ink)] disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:translate-y-0 disabled:hover:shadow-none", children: [_jsx("span", { className: "flex min-w-0 flex-1 items-center gap-2 text-left", children: _jsx("span", { className: "truncate", children: opt.text }) }), opt.mode === "brave" && opt.check && (_jsxs(Chip, { size: "sm", tint: "var(--accent-amber)", children: ["D20 \u00B7 ", opt.check.attr, " \u2265 ", opt.check.dc] }))] }) }, opt.id))) })] })), decision?.kind === "cg_show" && (_jsx("div", { className: "absolute inset-0 z-40 bg-black", children: (() => {
                        const isVideo = detectMediaKind(decision.url) === "video";
                        const canContinue = !isVideo || !!cgVideoEnded;
                        return (_jsxs(_Fragment, { children: [decision.url && (_jsx(CgMedia, { url: decision.url, name: decision.name, language: language, onVideoEnded: onCgVideoEnded })), canContinue && (_jsx("div", { className: "absolute inset-x-0 flex justify-center", style: { bottom: "max(24px, env(safe-area-inset-bottom, 0px))" }, children: _jsx(PaperButton, { onClick: onCgContinue, disabled: !!blocked, size: "md", children: t(language, "player.continue") }) }))] }));
                    })() }))] }) }));
}
export default PlayerStage;
// NameTag is re-exported for parity with the online player's call sites that may
// want the pill outside the bubble (the preview doesn't use it directly, but
// keeping the surface avoids a second import path for integrators).
export { NameTag };
